import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Plus, Search, GraduationCap, Mail, Phone, Edit, Trash2, Users, ChevronDown, RefreshCw, Loader2, CheckCircle, AlertTriangle, X, AlertCircle, Key, Copy, Eye, EyeOff, Building2, Clock, FileCheck, XCircle, Filter, ClipboardCheck, FileEdit, Archive, RotateCcw, KeyRound, UserCog, Ban, ShieldCheck, Download, MoreVertical, FolderOpen, Hourglass } from 'lucide-react';
import { ResetDeadlineModal } from '@/shared/components/users/ResetDeadlineModal';
import {
  fetchStudentUsers,
  fetchAllStudentUsers,
  fetchManagementPermissions,
  resendVerificationEmail,
  resetProfileDeadline,
  archiveStudent,
  restoreStudent,
  deleteStudent,
  adminResetPassword,
  adminResetParentPassword,
  adminResetStudentTempPassword,
  suspendStudent,
  unsuspendStudent,
  getStudentCountsByAcademicYear,
  type StudentUser,
  type ManagementPermissions,
  type LinkedParent,
  type StudentFilters,
  type AdminResetParentPasswordResponse,
  type AdminResetStudentTempPasswordResponse,
  type HoldType,
} from '@/services/userService';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { usePermissions } from '@/features/auth/hooks/usePermissions';
import { Permission } from '@/types/global.types';
import { StudentFiltersPanel, FilterDropdown } from '@/shared/components/users/StudentFiltersPanel';
import { getPendingValidations, getValidationRequestByUserId, type PendingValidation } from '@/services/validationService';
import { getPendingChangeRequests, type ChangeRequest } from '@/services/changeRequestService';
import { fetchAcademicYears, type AcademicYear } from '@/services/academicPlanningService';
import { CreateStudentModal } from '@/shared/components/users/CreateStudentModal';
import { CreateParentModal } from '@/shared/components/users/CreateParentModal';
import { ProfileValidationModal } from '@/shared/components/users/ProfileValidationModal';
import { AdminChangeRequestsCard } from '@/shared/components/users/AdminChangeRequestsCard';
import { BulkImportStudentsModal } from '@/shared/components/users/BulkImportStudentsModal';
import { DirectEditStudentProfileModal } from '@/shared/components/users/DirectEditStudentProfileModal';
import { ViewStudentModal } from '@/shared/components/users/ViewStudentModal';
import { StudentCredentialsModal } from '@/shared/components/users/StudentCredentialsModal';
import { BulkExportCredentialsModal } from '@/shared/components/users/BulkExportCredentialsModal';
import { StudentPhysicalDocumentsTrackingModal } from '@/shared/components/users/StudentPhysicalDocumentsTrackingModal';
import { formatPhoneWithCountry } from '@/shared/constants/phoneCodes';
import { buildHaystack, matchesAllTokens } from '@/shared/utils/searchMatch';

const statusLabels: Record<string, { label: string; color: string }> = {
  ACTIVE: { label: 'Actif', color: 'bg-green-100 text-green-800' },
  PENDING: { label: 'En attente', color: 'bg-yellow-100 text-yellow-800' },
  INACTIVE: { label: 'Inactif', color: 'bg-gray-100 text-gray-800' },
  SUSPENDED: { label: 'Suspendu', color: 'bg-red-100 text-red-800' },
  WITHDRAWN: { label: 'Désistement', color: 'bg-orange-100 text-orange-800' },
};

const profileStatusLabels: Record<string, { label: string; color: string; icon: React.ElementType }> = {
  DRAFT: { label: 'Brouillon', color: 'bg-gray-100 text-gray-700', icon: Edit },
  PENDING_VALIDATION: { label: 'À valider', color: 'bg-orange-100 text-orange-800', icon: Clock },
  APPROVED: { label: 'Validé', color: 'bg-green-100 text-green-800', icon: FileCheck },
  REJECTED: { label: 'Rejeté', color: 'bg-red-100 text-red-800', icon: XCircle },
};

type ProfileStatusValue = 'PENDING_VALIDATION' | 'DRAFT' | 'APPROVED' | 'REJECTED';


export const StudentManagementPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAuthStore((state) => state.user);
  const fetchPermissions = useAuthStore((state) => state.fetchPermissions);
  const { hasPermission } = usePermissions();

  // Refresh permissions on mount to get latest permissions from API
  // This ensures users see updated permissions without needing to re-login
  useEffect(() => {
    fetchPermissions().catch(() => {
      // Silent fail - will use cached permissions
    });
  }, [fetchPermissions]);

  // Permission-based access control
  const canCreateStudents = hasPermission(Permission.STUDENT_CREATE);
  const canBulkCreateStudents = hasPermission(Permission.STUDENT_BULK_CREATE);
  const canValidateModifications = hasPermission(Permission.STUDENT_VALIDATE_MODIFICATIONS);
  const canEditStudentProfile = hasPermission(Permission.STUDENT_EDIT_PROFILE);
  const canDeleteStudents = hasPermission(Permission.STUDENT_DELETE);
  const canValidateProfiles = hasPermission(Permission.STUDENT_VALIDATE_PROFILE);
  const canArchiveStudents = hasPermission(Permission.STUDENT_ARCHIVE);
  const canResetPassword = hasPermission(Permission.STUDENT_RESET_PASSWORD);
  const canSuspendStudents = hasPermission(Permission.STUDENT_SUSPEND_ACTIVATE);

  // Check if user has limited view permissions (no validation features, limited table columns)
  // This is based on whether they can validate profiles, not on role
  const hasLimitedView = !canValidateProfiles && !canValidateModifications;
  const [students, setStudents] = useState<StudentUser[]>([]);
  const [permissions, setPermissions] = useState<ManagementPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const initialLoadDone = React.useRef(false);
  const allStudentsRef = React.useRef<StudentUser[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [profileStatusFilters, setProfileStatusFilters] = useState<ProfileStatusValue[]>([]);
  const [showWithdrawn, setShowWithdrawn] = useState(false); // Checkbox to show désistements

  // Track the validation request ID from URL
  const validationRequestId = searchParams.get('validation');

  // Dropdown state
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Modal states
  const [showStudentModal, setShowStudentModal] = useState(false);
  const [showParentModal, setShowParentModal] = useState(false);
  const [showBulkImportModal, setShowBulkImportModal] = useState(false);

  // Resend email states
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState<StudentUser | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [studentToResetDeadline, setStudentToResetDeadline] = useState<StudentUser | null>(null);
  const [resettingDeadline, setResettingDeadline] = useState(false);

  // Archive confirmation modal
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [studentToArchive, setStudentToArchive] = useState<StudentUser | null>(null);
  const [archiving, setArchiving] = useState(false);

  // Restore confirmation modal
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [studentToRestore, setStudentToRestore] = useState<StudentUser | null>(null);
  const [restoring, setRestoring] = useState(false);

  // Suspend confirmation modal
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [studentToSuspend, setStudentToSuspend] = useState<StudentUser | null>(null);
  const [suspending, setSuspending] = useState(false);
  const [selectedHoldType, setSelectedHoldType] = useState<HoldType>('ACADEMIC');
  const [suspendReason, setSuspendReason] = useState('');

  // Unsuspend confirmation modal
  const [showUnsuspendModal, setShowUnsuspendModal] = useState(false);
  const [studentToUnsuspend, setStudentToUnsuspend] = useState<StudentUser | null>(null);
  const [unsuspending, setUnsuspending] = useState(false);

  // Parent credentials modal
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [selectedParent, setSelectedParent] = useState<LinkedParent | null>(null);
  const [copiedField, setCopiedField] = useState<'username' | 'password' | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Validation modal state
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [selectedValidationId, setSelectedValidationId] = useState<string | null>(null);
  const [pendingValidations, setPendingValidations] = useState<PendingValidation[]>([]);

  // Change requests state
  const [pendingChangeRequests, setPendingChangeRequests] = useState<ChangeRequest[]>([]);
  const [showChangeRequestsSection, setShowChangeRequestsSection] = useState(false);

  // View student details modal
  const [showViewStudentModal, setShowViewStudentModal] = useState(false);
  const [studentToView, setStudentToView] = useState<StudentUser | null>(null);

  // Direct edit student profile modal
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [studentToEdit, setStudentToEdit] = useState<StudentUser | null>(null);

  // Password reset modal - Step 1: Choose student or parent
  const [showResetPasswordChoiceModal, setShowResetPasswordChoiceModal] = useState(false);
  const [studentForPasswordReset, setStudentForPasswordReset] = useState<StudentUser | null>(null);

  // Password reset modal - Step 2: Student password reset (email flow)
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [studentToResetPassword, setStudentToResetPassword] = useState<StudentUser | null>(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] = useState<string | null>(null);
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);

  // Password reset modal - Parent password reset (temp password flow)
  const [showResetParentPasswordModal, setShowResetParentPasswordModal] = useState(false);
  const [parentToResetPassword, setParentToResetPassword] = useState<LinkedParent | null>(null);
  const [parentResetResult, setParentResetResult] = useState<AdminResetParentPasswordResponse | null>(null);
  const [resettingParentPassword, setResettingParentPassword] = useState(false);
  const [resetParentPasswordError, setResetParentPasswordError] = useState<string | null>(null);
  const [showParentTempPassword, setShowParentTempPassword] = useState(false);
  const [copiedParentField, setCopiedParentField] = useState<'username' | 'password' | null>(null);

  // Password reset modal - Student temp password (temp password flow)
  const [showResetStudentTempPasswordModal, setShowResetStudentTempPasswordModal] = useState(false);
  const [studentTempResetResult, setStudentTempResetResult] = useState<AdminResetStudentTempPasswordResponse | null>(null);
  const [resettingStudentTempPassword, setResettingStudentTempPassword] = useState(false);
  const [resetStudentTempPasswordError, setResetStudentTempPasswordError] = useState<string | null>(null);
  const [showStudentTempPassword, setShowStudentTempPassword] = useState(false);
  const [copiedStudentField, setCopiedStudentField] = useState<'email' | 'password' | 'username' | null>(null);

  // Student credentials modal - for students without email who haven't logged in yet
  const [showStudentCredentialsModal, setShowStudentCredentialsModal] = useState(false);
  const [studentForCredentials, setStudentForCredentials] = useState<StudentUser | null>(null);

  // Bulk export credentials modal
  const [showBulkExportModal, setShowBulkExportModal] = useState(false);

  // Physical documents tracking modal
  const [showPhysicalDocsModal, setShowPhysicalDocsModal] = useState(false);
  const [studentForPhysicalDocs, setStudentForPhysicalDocs] = useState<StudentUser | null>(null);

  // Actions dropdown state
  const [openActionsDropdown, setOpenActionsDropdown] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; showAbove: boolean }>({ top: 0, left: 0, showAbove: false });
  const actionsDropdownRef = useRef<HTMLDivElement>(null);
  const dropdownButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // Note: Legacy campus filter state removed - now using advanced filters

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState<StudentFilters>({});
  const [allStudents, setAllStudents] = useState<StudentUser[]>([]); // Store all students for filter options

  // Server-side pagination + search
  const [page, setPage] = useState(1);
  const [totalStudents, setTotalStudents] = useState(0);
  const [pendingValidationCount, setPendingValidationCount] = useState(0);
  const [withdrawnCount, setWithdrawnCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [serverSearch, setServerSearch] = useState('');
  const [academicYears, setAcademicYears] = useState<AcademicYear[]>([]); // All academic years for filter options
  // Headcount per year from the enrolment history — a year keeps the students
  // who studied it after they were promoted, graduated or excluded.
  const [academicYearCounts, setAcademicYearCounts] = useState<Record<string, number>>({});
  const [currentAcademicYear, setCurrentAcademicYear] = useState<AcademicYear | null>(null);
  const [academicYearInitialized, setAcademicYearInitialized] = useState(false);

  // Load current academic year and set as default filter
  useEffect(() => {
    const loadCurrentAcademicYear = async () => {
      const tokens = useAuthStore.getState().tokens;
      if (!tokens?.accessToken) return;

      try {
        const years = await fetchAcademicYears(tokens.accessToken);
        setAcademicYears(years); // Store all academic years for filter options

        // Per-year headcounts come from the server: the loaded page only knows
        // where each student stands today, so it cannot count a past year.
        getStudentCountsByAcademicYear()
          .then((rows) =>
            setAcademicYearCounts(
              Object.fromEntries(rows.map((r) => [r.academicYearId, r.count])),
            ),
          )
          .catch(() => undefined);
        const current = years.find(y => y.isCurrent);
        if (current) {
          setCurrentAcademicYear(current);
          // Pre-select current academic year in filters
          setAdvancedFilters(prev => ({
            ...prev,
            academicYearId: [current.id]
          }));
        }
        setAcademicYearInitialized(true);
      } catch (error) {
        console.error('Error loading academic years:', error);
        setAcademicYearInitialized(true);
      }
    };

    loadCurrentAcademicYear();
  }, []);

  // Load data after academic year is initialized
  useEffect(() => {
    if (academicYearInitialized) {
      loadData(currentAcademicYear ? true : false);
    }
  }, [academicYearInitialized]);

  // Auto-filter to pending validation when coming from a notification
  useEffect(() => {
    if (validationRequestId) {
      setProfileStatusFilters(['PENDING_VALIDATION']);
      // Clear the validation param from URL after applying filter
      setSearchParams({}, { replace: true });
    }
  }, [validationRequestId, setSearchParams]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Calculate dropdown position when opening actions menu
  const handleOpenActionsDropdown = useCallback((studentId: string, buttonElement: HTMLButtonElement) => {
    if (openActionsDropdown === studentId) {
      setOpenActionsDropdown(null);
      return;
    }

    const rect = buttonElement.getBoundingClientRect();
    const dropdownHeight = 400; // Approximate max dropdown height
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;

    setDropdownPosition({
      top: showAbove ? rect.top : rect.bottom + 4,
      left: rect.right - 224, // 224px = dropdown width (w-56 = 14rem = 224px)
      showAbove,
    });
    setOpenActionsDropdown(studentId);
  }, [openActionsDropdown]);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        if (!target.closest('[data-dropdown-trigger]')) {
          setOpenActionsDropdown(null);
        }
      }
    };

    const handleScroll = () => {
      setOpenActionsDropdown(null);
    };

    if (openActionsDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [openActionsDropdown]);

  const loadData = async (applyFilters = false) => {
    try {
      // Full-page spinner only on the very first load; afterwards keep the
      // table on screen and show a lightweight "searching" indicator instead.
      if (!initialLoadDone.current) {
        setLoading(true);
      } else {
        setSearching(true);
      }
      setError(null);

      // Determine which filters to apply (always paginated server-side)
      // A past year is history: its withdrawn students did study that year
      // and belong to its list, so they are always included there.
      const viewingPastYear =
        (advancedFilters.academicYearId?.length ?? 0) > 0 &&
        !advancedFilters.academicYearId?.includes(currentAcademicYear?.id ?? '');
      const filtersToApply: StudentFilters = {
        ...(applyFilters ? advancedFilters : {}),
        ...(serverSearch ? { search: serverSearch } : {}),
        // Status filter and désistements are resolved by the server so they
        // span every page, not only the 50 rows loaded.
        ...(profileStatusFilters.length > 0 ? { profileSubmissionStatus: profileStatusFilters } : {}),
        includeWithdrawn: showWithdrawn || viewingPastYear,
        page,
        limit: 50,
      };

      // The all-students walk (filter options + exports) does not depend on the
      // search term — fetch it once, then reuse it on search/page changes.
      const needAllStudents = applyFilters && (allStudentsRef.current.length === 0 || !serverSearch);

      const [studentsResponse, allStudentsResponse, permissionsData, validationsData, changeRequestsData] = await Promise.all([
        fetchStudentUsers(filtersToApply),
        needAllStudents ? fetchAllStudentUsers() : Promise.resolve(null),
        fetchManagementPermissions().catch((err) => {
          console.error('Error fetching management permissions:', err);
          return null; // Return null on error, permission checks will use fallback
        }),
        getPendingValidations().catch(() => []), // Don't fail if validations can't be loaded
        getPendingChangeRequests().catch(() => []), // Don't fail if change requests can't be loaded
      ]);

      // Extract data arrays from paginated responses (with safety fallbacks)
      const studentsData = studentsResponse?.data || [];

      setStudents(studentsData);
      setTotalStudents(studentsResponse?.meta?.total ?? studentsData.length);
      setTotalPages(studentsResponse?.meta?.totalPages ?? 1);
      setPendingValidationCount(studentsResponse?.meta?.pendingValidationTotal ?? 0);
      setWithdrawnCount(studentsResponse?.meta?.withdrawnTotal ?? 0);
      setPermissions(permissionsData);
      setPendingValidations(validationsData);
      setPendingChangeRequests(changeRequestsData);

      // Store all students for filter option calculation (null = fetch skipped, keep previous)
      if (allStudentsResponse) {
        allStudentsRef.current = allStudentsResponse;
        setAllStudents(allStudentsResponse);
      } else if (!applyFilters) {
        allStudentsRef.current = studentsData;
        setAllStudents(studentsData);
      }
      initialLoadDone.current = true;
    } catch (err) {
      console.error('Error fetching student data:', err);
      setError(err instanceof Error ? err.message : 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  // Debounce the search input into a server-side search term
  useEffect(() => {
    const timer = setTimeout(() => setServerSearch(searchQuery.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reload when filters or search change (back to page 1), or when page changes
  useEffect(() => {
    if (!academicYearInitialized) return;
    if (page !== 1) {
      setPage(1); // triggers the page effect below, which reloads
      return;
    }
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, serverSearch, profileStatusFilters, showWithdrawn]);

  useEffect(() => {
    if (!academicYearInitialized) return;
    loadData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  const handleCreateSuccess = () => {
    loadData(); // Refresh the list
  };

  // Find validation request ID for a student
  const getValidationRequestId = (userId: string): string | null => {
    const validation = pendingValidations.find(v => v.userId === userId);
    return validation?.id || null;
  };

  // Handle opening validation modal
  const handleOpenValidation = async (userId: string) => {
    // First try from cache
    let validationId = getValidationRequestId(userId);

    // If not in cache, fetch from API
    if (!validationId) {
      try {
        validationId = await getValidationRequestByUserId(userId);
      } catch (err) {
        console.error('Error fetching validation request:', err);
      }
    }

    if (validationId) {
      setSelectedValidationId(validationId);
      setShowValidationModal(true);
    } else {
      console.error('No validation request found for user:', userId);
    }
  };

  // Handle validation complete
  const handleValidationComplete = () => {
    loadData(); // Refresh to update statuses
  };

  const handleResendEmail = async (userId: string) => {
    setResendingUserId(userId);
    setResendSuccess(null);
    setResendError(null);

    try {
      const result = await resendVerificationEmail(userId);
      setResendSuccess(result.message);
      // Clear success message after 5 seconds
      setTimeout(() => setResendSuccess(null), 5000);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Erreur lors de l\'envoi');
      // Clear error message after 5 seconds
      setTimeout(() => setResendError(null), 5000);
    } finally {
      setResendingUserId(null);
    }
  };

  const handleDeleteClick = (student: StudentUser) => {
    setStudentToDelete(student);
    setShowDeleteModal(true);
  };

  const handleEditProfileClick = (student: StudentUser) => {
    setStudentToEdit(student);
    setShowEditProfileModal(true);
  };

  const handlePhysicalDocsClick = (student: StudentUser) => {
    setStudentForPhysicalDocs(student);
    setShowPhysicalDocsModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!studentToDelete) return;

    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteStudent(studentToDelete.id);
      setShowDeleteModal(false);
      setStudentToDelete(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error deleting student:', error);
      setDeleteError(error instanceof Error ? error.message : 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setStudentToDelete(null);
    setDeleteError(null);
  };

  const handleConfirmResetDeadline = async () => {
    if (!studentToResetDeadline) return;
    setResettingDeadline(true);
    setResendSuccess(null);
    setResendError(null);
    try {
      const status = await resetProfileDeadline(studentToResetDeadline.id);
      const until = status.deadlineDate ? new Date(status.deadlineDate).toLocaleDateString('fr-FR') : null;
      setResendSuccess(
        `Délai de soumission réinitialisé pour ${studentToResetDeadline.firstName} ${studentToResetDeadline.lastName}${until ? ` (jusqu'au ${until})` : ''}.`,
      );
      setStudentToResetDeadline(null);
      loadData();
      setTimeout(() => setResendSuccess(null), 6000);
    } catch (error) {
      setResendError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du délai');
      setTimeout(() => setResendError(null), 6000);
    } finally {
      setResettingDeadline(false);
    }
  };

  // Archive handlers
  const handleArchiveClick = (student: StudentUser) => {
    setStudentToArchive(student);
    setShowArchiveModal(true);
  };

  const handleConfirmArchive = async () => {
    if (!studentToArchive) return;

    setArchiving(true);
    try {
      await archiveStudent(studentToArchive.id);
      setShowArchiveModal(false);
      setStudentToArchive(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error archiving student:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'archivage');
    } finally {
      setArchiving(false);
    }
  };

  const handleCancelArchive = () => {
    setShowArchiveModal(false);
    setStudentToArchive(null);
  };

  // Restore handlers
  const handleRestoreClick = (student: StudentUser) => {
    setStudentToRestore(student);
    setShowRestoreModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!studentToRestore) return;

    setRestoring(true);
    try {
      await restoreStudent(studentToRestore.id);
      setShowRestoreModal(false);
      setStudentToRestore(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error restoring student:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la restauration');
    } finally {
      setRestoring(false);
    }
  };

  const handleCancelRestore = () => {
    setShowRestoreModal(false);
    setStudentToRestore(null);
  };

  // Suspend handlers
  const handleSuspendClick = (student: StudentUser) => {
    setStudentToSuspend(student);
    setSelectedHoldType('ACADEMIC');
    setSuspendReason('');
    setShowSuspendModal(true);
  };

  const handleConfirmSuspend = async () => {
    if (!studentToSuspend) return;

    setSuspending(true);
    try {
      await suspendStudent(studentToSuspend.id, {
        holdType: selectedHoldType,
        reason: suspendReason || undefined,
      });
      setShowSuspendModal(false);
      setStudentToSuspend(null);
      setSuspendReason('');
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error suspending student:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la suspension');
    } finally {
      setSuspending(false);
    }
  };

  const handleCancelSuspend = () => {
    setShowSuspendModal(false);
    setStudentToSuspend(null);
    setSuspendReason('');
  };

  // Unsuspend handlers
  const handleUnsuspendClick = (student: StudentUser) => {
    setStudentToUnsuspend(student);
    setShowUnsuspendModal(true);
  };

  const handleConfirmUnsuspend = async () => {
    if (!studentToUnsuspend) return;

    setUnsuspending(true);
    try {
      await unsuspendStudent(studentToUnsuspend.id);
      setShowUnsuspendModal(false);
      setStudentToUnsuspend(null);
      loadData(); // Refresh the list
    } catch (error) {
      console.error('Error unsuspending student:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de la levée de suspension');
    } finally {
      setUnsuspending(false);
    }
  };

  const handleCancelUnsuspend = () => {
    setShowUnsuspendModal(false);
    setStudentToUnsuspend(null);
  };

  // Password reset handlers - Show choice modal only if student has email
  const handleResetPasswordClick = (student: StudentUser) => {
    setStudentForPasswordReset(student);

    // If student has no email, skip choice modal and go directly to temp password
    if (!student.email) {
      setStudentToResetPassword(student);
      setShowResetStudentTempPasswordModal(true);
      setStudentTempResetResult(null);
      setResetStudentTempPasswordError(null);
      setShowStudentTempPassword(false);
      setCopiedStudentField(null);
    } else {
      // Show choice modal for students with email
      setShowResetPasswordChoiceModal(true);
    }
  };

  // Handle choice: reset student password via email
  const handleChooseStudentEmailReset = () => {
    if (!studentForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStudentToResetPassword(studentForPasswordReset);
    setShowResetPasswordModal(true);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Handle choice: reset student password with temp password
  const handleChooseStudentTempPasswordReset = () => {
    if (!studentForPasswordReset) return;
    setShowResetPasswordChoiceModal(false);
    setStudentToResetPassword(studentForPasswordReset);
    setShowResetStudentTempPasswordModal(true);
    setStudentTempResetResult(null);
    setResetStudentTempPasswordError(null);
    setShowStudentTempPassword(false);
    setCopiedStudentField(null);
  };

  // Handle choice: reset parent password
  const handleChooseParentReset = (parent: LinkedParent) => {
    setShowResetPasswordChoiceModal(false);
    setParentToResetPassword(parent);
    setShowResetParentPasswordModal(true);
    setParentResetResult(null);
    setResetParentPasswordError(null);
    setShowParentTempPassword(false);
    setCopiedParentField(null);
  };

  const handleCancelPasswordChoice = () => {
    setShowResetPasswordChoiceModal(false);
    setStudentForPasswordReset(null);
  };

  const handleConfirmResetPassword = async () => {
    if (!studentToResetPassword) return;

    setResettingPassword(true);
    setResetPasswordError(null);
    try {
      const result = await adminResetPassword(studentToResetPassword.id);
      setResetPasswordSuccess(result.message);
      // Close modal after a short delay to show success message
      setTimeout(() => {
        setShowResetPasswordModal(false);
        setStudentToResetPassword(null);
        setStudentForPasswordReset(null);
        setResetPasswordSuccess(null);
      }, 2000);
    } catch (error) {
      console.error('Error resetting password:', error);
      setResetPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingPassword(false);
    }
  };

  const handleCancelResetPassword = () => {
    setShowResetPasswordModal(false);
    setStudentToResetPassword(null);
    setStudentForPasswordReset(null);
    setResetPasswordSuccess(null);
    setResetPasswordError(null);
  };

  // Student temp password reset handlers
  const handleConfirmResetStudentTempPassword = async () => {
    if (!studentToResetPassword) return;

    setResettingStudentTempPassword(true);
    setResetStudentTempPasswordError(null);
    try {
      const result = await adminResetStudentTempPassword(studentToResetPassword.id);
      setStudentTempResetResult(result);
    } catch (error) {
      console.error('Error resetting student temp password:', error);
      setResetStudentTempPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingStudentTempPassword(false);
    }
  };

  const handleCancelResetStudentTempPassword = () => {
    setShowResetStudentTempPasswordModal(false);
    setStudentToResetPassword(null);
    setStudentForPasswordReset(null);
    setStudentTempResetResult(null);
    setResetStudentTempPasswordError(null);
    setShowStudentTempPassword(false);
    setCopiedStudentField(null);
  };

  const copyStudentCredential = async (text: string, field: 'email' | 'password' | 'username') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStudentField(field);
      setTimeout(() => setCopiedStudentField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  // Parent password reset handlers
  const handleConfirmResetParentPassword = async () => {
    if (!parentToResetPassword) return;

    setResettingParentPassword(true);
    setResetParentPasswordError(null);
    try {
      const result = await adminResetParentPassword(parentToResetPassword.id);
      setParentResetResult(result);
    } catch (error) {
      console.error('Error resetting parent password:', error);
      setResetParentPasswordError(error instanceof Error ? error.message : 'Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResettingParentPassword(false);
    }
  };

  const handleCancelResetParentPassword = () => {
    setShowResetParentPasswordModal(false);
    setParentToResetPassword(null);
    setStudentForPasswordReset(null);
    setParentResetResult(null);
    setResetParentPasswordError(null);
    setShowParentTempPassword(false);
    setCopiedParentField(null);
  };

  const copyParentCredential = async (text: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedParentField(field);
      setTimeout(() => setCopiedParentField(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const handleShowCredentials = (parent: LinkedParent) => {
    setSelectedParent(parent);
    setShowCredentialsModal(true);
    setShowPassword(false);
    setCopiedField(null);
  };

  const handleCloseCredentials = () => {
    setShowCredentialsModal(false);
    setSelectedParent(null);
    setShowPassword(false);
    setCopiedField(null);
  };

  const copyToClipboard = async (text: string, field: 'username' | 'password') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const filteredStudents = useMemo(() => {
    if (!students) return [];
    // Looking at a year that is over is looking at history: the students who
    // were excluded during it did study that year and belong to its list. The
    // « désistements » checkbox keeps its meaning on the year in progress,
    // where an archived account means someone who has left.
    const viewingPastYear =
      (advancedFilters.academicYearId?.length ?? 0) > 0 &&
      !advancedFilters.academicYearId?.includes(currentAcademicYear?.id ?? '');

    return students.filter(s => {
      // Filter out withdrawn students unless showWithdrawn is checked
      if (!showWithdrawn && !viewingPastYear && s.status === 'WITHDRAWN') {
        return false;
      }

      // Filter by profile status (if any selected)
      if (profileStatusFilters.length > 0) {
        if (!s.profileSubmissionStatus || !profileStatusFilters.includes(s.profileSubmissionStatus as ProfileStatusValue)) {
          return false;
        }
      }

      // Search filter — token-based, same rule as the backend (every word must
      // match), so "Prénom Nom", "Nom Prénom", trailing spaces and mixed case
      // all work and never contradict the server results.
      if (searchQuery.trim()) {
        const haystack = buildHaystack([
          s.firstName,
          s.lastName,
          s.email,
          s.username,
          ...(s.linkedParents?.flatMap(p => [p.firstName, p.lastName]) ?? []),
        ]);
        if (!matchesAllTokens(searchQuery, haystack)) {
          return false;
        }
      }

      return true;
    })
    // Always display alphabetically by last name, then first name.
    .sort((a, b) => {
      const byLast = (a.lastName || '').localeCompare(b.lastName || '', 'fr', { sensitivity: 'base' });
      return byLast !== 0
        ? byLast
        : (a.firstName || '').localeCompare(b.firstName || '', 'fr', { sensitivity: 'base' });
    });
  }, [students, searchQuery, profileStatusFilters, showWithdrawn, advancedFilters.academicYearId, currentAcademicYear]);

  // Pending dossiers and withdrawn students are counted by the server over
  // the whole scope (see loadData), never over the current page only.

  // Count active students (excluding WITHDRAWN)
  const activeStudentCount = useMemo(() =>
    (students || []).filter(s => s.status !== 'WITHDRAWN').length
  , [students]);

  // Count parents over the FULL student set (allStudents), not just the current page
  const parentCount = (allStudents.length > 0 ? allStudents : students || []).reduce(
    (count, s) => count + (s.linkedParents?.length || 0),
    0,
  );

  // Get unique campuses for filter dropdown
  const availableCampuses = useMemo(() => {
    const campusMap = new Map<string, { id: string; name: string; city: string }>();
    (students || []).forEach(s => {
      if (s.campus) {
        campusMap.set(s.campus.id, s.campus);
      }
    });
    return Array.from(campusMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [students]);

  // Check if we have multiple campuses to show the campus column and filter
  const hasMultipleCampuses = availableCampuses.length > 1;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  // Show error state if API call failed
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-800 mb-4">{error}</p>
        <button
          onClick={() => loadData()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <RefreshCw size={16} />
          Réessayer
        </button>
      </div>
    );
  }

  // Show permissions error only if we have permissions data but user can't manage students
  if (permissions && !permissions.canManageStudents) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
        <p className="text-yellow-800">
          Vous n'avez pas les permissions pour gérer les étudiants.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Étudiants</h1>
          <p className="text-gray-500 mt-1">
            Gérez les étudiants et parents/tuteurs de votre établissement
          </p>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          {/* Bulk Export Button - restricted to roles that manage student accounts */}
          {canCreateStudents && (
            <button
              onClick={() => setShowBulkExportModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              title="Exporter les identifiants étudiants"
            >
              <Download size={18} />
              <span className="hidden sm:inline">Exporter identifiants</span>
            </button>
          )}

          {/* Add Button with Dropdown - Only shown if user can create students */}
          {canCreateStudents && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                <Plus size={20} />
                <span>Ajouter</span>
                <ChevronDown size={16} className={`transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
              </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowStudentModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <div className="p-2 bg-green-100 rounded-lg">
                    <GraduationCap size={18} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Étudiant</p>
                    <p className="text-xs text-gray-500">Créer un compte étudiant</p>
                  </div>
                </button>
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    setShowParentModal(true);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 text-left"
                >
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <Users size={18} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Parent / Tuteur</p>
                    <p className="text-xs text-gray-500">Créer et lier à un étudiant</p>
                  </div>
                </button>
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {/* Success/Error Messages */}
      {resendSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="text-green-600" size={20} />
          <p className="text-green-800">{resendSuccess}</p>
        </div>
      )}
      {resendError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <Mail className="text-red-600" size={20} />
          <p className="text-red-800">{resendError}</p>
        </div>
      )}

      {/* Stats Cards */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${hasLimitedView ? 'lg:grid-cols-2' : 'lg:grid-cols-4'} gap-4`}>
        {/* Étudiants Actifs Card - Clickable to clear all profile filters and show all active students */}
        <button
          onClick={() => {
            // Clear profile status filters to show all active students (non-withdrawn)
            setProfileStatusFilters([]);
            setShowChangeRequestsSection(false);
          }}
          className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
            profileStatusFilters.length === 0 && !showChangeRequestsSection
              ? 'border-green-500 ring-2 ring-green-200'
              : 'border-gray-200 hover:border-green-300'
          }`}
        >
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <GraduationCap size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Étudiants Actifs</p>
              <p className="text-2xl font-bold text-gray-900">{totalStudents || activeStudentCount}</p>
            </div>
          </div>
        </button>

        {/* Parents/Tuteurs Card */}
        <div className="bg-white rounded-xl p-4 border border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Parents/Tuteurs</p>
              <p className="text-2xl font-bold text-gray-900">{parentCount}</p>
            </div>
          </div>
        </div>

        {/* Dossiers à valider Card - Clickable (only if user can validate profiles) */}
        {canValidateProfiles && (
          <button
            onClick={() => {
              const isPendingSelected = profileStatusFilters.length === 1 && profileStatusFilters[0] === 'PENDING_VALIDATION';
              setProfileStatusFilters(isPendingSelected ? [] : ['PENDING_VALIDATION']);
              setShowChangeRequestsSection(false);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              profileStatusFilters.length === 1 && profileStatusFilters[0] === 'PENDING_VALIDATION' && !showChangeRequestsSection
                ? 'border-orange-500 ring-2 ring-orange-200'
                : 'border-gray-200 hover:border-orange-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingValidationCount > 0 ? 'bg-orange-100' : 'bg-gray-100'}`}>
                <Clock size={24} className={pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Dossiers à valider</p>
                <p className={`text-2xl font-bold ${pendingValidationCount > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                  {pendingValidationCount}
                </p>
              </div>
            </div>
          </button>
        )}

        {/* Demandes de modification Card - Clickable (only if user can validate modifications) */}
        {canValidateModifications && (
          <button
            onClick={() => {
              setShowChangeRequestsSection(prev => !prev);
            }}
            className={`bg-white rounded-xl p-4 border-2 transition-all text-left ${
              showChangeRequestsSection
                ? 'border-blue-500 ring-2 ring-blue-200'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${pendingChangeRequests.length > 0 ? 'bg-blue-100' : 'bg-gray-100'}`}>
                <FileEdit size={24} className={pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-400'} />
              </div>
              <div>
                <p className="text-sm text-gray-500">Demandes de modification</p>
                <p className={`text-2xl font-bold ${pendingChangeRequests.length > 0 ? 'text-blue-600' : 'text-gray-900'}`}>
                  {pendingChangeRequests.length}
                </p>
              </div>
            </div>
          </button>
        )}

        </div>

      {/* Admin Change Requests Section - Shown when toggled (replaces table and filters) */}
      {showChangeRequestsSection ? (
        <AdminChangeRequestsCard userTypeFilter="student" onRequestReviewed={() => loadData()} />
      ) : (
        <>
          {/* Search Bar */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Rechercher par nom d'étudiant, email, ou nom de parent..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              {searching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              )}
            </div>
            {/* Profile Status Filter - Checkbox Dropdown */}
            <FilterDropdown
              label="Statut du profil"
              icon={Filter}
              options={[
                { value: 'PENDING_VALIDATION', label: 'À valider' },
                { value: 'DRAFT', label: 'Brouillon' },
                { value: 'APPROVED', label: 'Validés' },
                { value: 'REJECTED', label: 'Rejetés' },
              ]}
              selectedValues={profileStatusFilters}
              onChange={(values) => setProfileStatusFilters(values as ProfileStatusValue[])}
              placeholder="Tous les profils"
            />

            {/* Show Withdrawn Students Checkbox */}
            {canArchiveStudents && (
              <label className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={showWithdrawn}
                  onChange={(e) => setShowWithdrawn(e.target.checked)}
                  className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700">
                  Afficher les désistements
                  {withdrawnCount > 0 && (
                    <span className="ml-1 text-orange-600 font-medium">({withdrawnCount})</span>
                  )}
                </span>
              </label>
            )}
          </div>

          {/* Advanced Filters Panel */}
          <StudentFiltersPanel
            filters={advancedFilters}
            onFiltersChange={setAdvancedFilters}
            studentData={allStudents.length > 0 ? allStudents : students}
            availableAcademicYears={academicYears}
            academicYearCounts={academicYearCounts}
          />

          {/* Active Filter Badges - Profile Status */}
          {profileStatusFilters.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-500">Filtres actifs:</span>
              {profileStatusFilters.map((status) => (
                <span
                  key={status}
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${profileStatusLabels[status]?.color}`}
                >
                  {React.createElement(profileStatusLabels[status]?.icon, { size: 14 })}
                  {profileStatusLabels[status]?.label}
                  <button
                    onClick={() => setProfileStatusFilters(profileStatusFilters.filter(s => s !== status))}
                    className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
              <span className="text-sm text-gray-500">
                ({filteredStudents.length} affiché{filteredStudents.length > 1 ? 's' : ''}{totalStudents > filteredStudents.length ? ` · ${totalStudents} au total` : ''})
              </span>
            </div>
          )}

          {/* Students Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom et Prénom
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Filière / Diplôme
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Niveau
                </th>
                {hasMultipleCampuses && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campus
                  </th>
                )}
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Parent / Tuteur
                </th>
                {!hasLimitedView && (
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                )}
                {(canEditStudentProfile || canDeleteStudents || canArchiveStudents) && (
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStudents.length === 0 ? (
                <tr>
                  <td colSpan={
                    5 + // Nom, Contact, Filière/Diplôme, Niveau, Parent/Tuteur (always shown)
                    (hasMultipleCampuses ? 1 : 0) + // Campus column
                    (!hasLimitedView ? 1 : 0) + // Statut column
                    ((canEditStudentProfile || canDeleteStudents || canArchiveStudents) ? 1 : 0) // Actions column
                  } className="px-6 py-12 text-center text-gray-500">
                    <GraduationCap className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                    {profileStatusFilters.length > 0 ? (
                      <>
                        <p>Aucun étudiant avec ce statut de profil</p>
                        <button
                          onClick={() => setProfileStatusFilters([])}
                          className="text-sm text-primary-600 hover:underline mt-2"
                        >
                          Afficher tous les étudiants
                        </button>
                      </>
                    ) : (
                      <>
                        <p>Aucun étudiant ou parent trouvé</p>
                        <p className="text-sm mt-2">
                          Cliquez sur "Ajouter" pour créer un nouveau compte
                        </p>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                filteredStudents.map((student) => (
                  <tr key={student.id} className="hover:bg-gray-50">
                    {/* Nom et Prénom - Last name + First name with login username below */}
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {student.lastName} {student.firstName}
                      </div>
                      {student.username && (
                        <div className="text-xs text-gray-500">
                          {student.username}
                        </div>
                      )}
                    </td>

                    {/* Contact - Phone + Email on same line */}
                    <td className="px-3 py-4">
                      <div className="text-sm">
                        {student.phone && (
                          <div className="flex items-center gap-1 text-gray-900">
                            <Phone size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate">{formatPhoneWithCountry(student.phone, student.phoneCountry)}</span>
                          </div>
                        )}
                        {student.email && (
                          <div className="flex items-center gap-1 text-gray-500 text-xs">
                            <Mail size={12} className="text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[180px]">{student.email}</span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Filière / Diplôme */}
                    <td className="px-3 py-4">
                      <div className="text-sm">
                        {student.filiereInscrit ? (
                          <>
                            <div className="text-gray-900 truncate max-w-[150px]" title={student.filiereInscrit}>
                              {student.filiereInscrit}
                            </div>
                            {student.diplomeInscrit && (
                              <div className="text-xs text-gray-500 truncate max-w-[150px]" title={student.diplomeInscrit}>
                                {student.diplomeInscrit}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </div>
                    </td>

                    {/* Niveau */}
                    <td className="px-3 py-4">
                      {student.niveauFormation ? (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {student.niveauFormation === 1 ? '1ère année' : `${student.niveauFormation}ème année`}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>

                    {/* Campus - Only shown if multiple campuses exist */}
                    {hasMultipleCampuses && (
                      <td className="px-3 py-4">
                        {student.campus ? (
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="text-gray-400 flex-shrink-0" />
                            <div className="text-sm text-gray-900 truncate max-w-[120px]" title={`${student.campus.name} - ${student.campus.city}`}>
                              {student.campus.name}
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">—</span>
                        )}
                      </td>
                    )}

                    {/* Parent / Tuteur - Compact display with phone */}
                    <td className="px-3 py-4">
                      {student.linkedParents && student.linkedParents.length > 0 ? (
                        <div className="space-y-2">
                          {student.linkedParents.map((parent, idx) => (
                            <div key={idx} className="flex flex-col">
                              <div className="flex items-center gap-1.5 text-sm">
                                <span className="text-gray-900 truncate max-w-[140px]" title={`${parent.firstName} ${parent.lastName}`}>
                                  {parent.firstName} {parent.lastName}
                                </span>
                                {parent.status === 'ACTIVE' && (
                                  <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800">Actif</span>
                                )}
                                {parent.status === 'PENDING' && parent.username && parent.tempPassword && (
                                  <button
                                    onClick={() => handleShowCredentials(parent)}
                                    className="p-0.5 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                                    title="Voir les identifiants"
                                  >
                                    <Key size={12} />
                                  </button>
                                )}
                              </div>
                              {parent.phone && (
                                <span className="text-xs text-gray-500 mt-0.5">{formatPhoneWithCountry(parent.phone, parent.phoneCountry || undefined)}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Statut - hidden for Marketing/Commercial/Financial managers */}
                    {!hasLimitedView && (
                      <td className="px-3 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {/* Account status badge */}
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusLabels[student.status]?.color || 'bg-gray-100 text-gray-800'}`}>
                            {statusLabels[student.status]?.label || student.status}
                          </span>
                          {/* Resend activation email button for PENDING status - only if user has email */}
                          {student.status === 'PENDING' && student.email && (
                            <button
                              onClick={() => handleResendEmail(student.id)}
                              disabled={resendingUserId === student.id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors disabled:opacity-50"
                              title="Renvoyer le lien d'activation"
                            >
                              {resendingUserId === student.id ? (
                                <Loader2 size={12} className="animate-spin" />
                              ) : (
                                <RefreshCw size={12} />
                              )}
                              Renvoyer
                            </button>
                          )}
                          {/* Credentials button for students without email who haven't logged in yet */}
                          {student.tempPassword && !student.lastLoginAt && (
                            <button
                              onClick={() => {
                                setStudentForCredentials(student);
                                setShowStudentCredentialsModal(true);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded hover:bg-amber-100 transition-colors"
                              title="Voir les identifiants de connexion"
                            >
                              <Key size={12} />
                              Identifiants
                            </button>
                          )}
                          {/* Profile status badge */}
                          {student.profileSubmissionStatus && profileStatusLabels[student.profileSubmissionStatus] && (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${profileStatusLabels[student.profileSubmissionStatus].color}`}>
                              {React.createElement(profileStatusLabels[student.profileSubmissionStatus].icon, { size: 10 })}
                              {profileStatusLabels[student.profileSubmissionStatus].label}
                            </span>
                          )}
                          {/* Validation button for PENDING_VALIDATION status - hide for user's own profile */}
                          {canValidateProfiles && student.profileSubmissionStatus === 'PENDING_VALIDATION' && student.id !== currentUser?.id && (
                            <button
                              onClick={() => handleOpenValidation(student.id)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 transition-colors"
                              title="Valider le profil"
                            >
                              <ClipboardCheck size={12} />
                              Valider
                            </button>
                          )}
                          {/* Restart the submission deadline for profiles still to be submitted */}
                          {canValidateProfiles && (student.profileSubmissionStatus === 'DRAFT' || student.profileSubmissionStatus === 'REJECTED') && student.id !== currentUser?.id && (
                            <button
                              onClick={() => setStudentToResetDeadline(student)}
                              className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                              title="Réinitialiser le délai de soumission du profil"
                            >
                              <Hourglass size={12} />
                              Délai
                            </button>
                          )}
                        </div>
                      </td>
                    )}

                    {/* Actions - Three-dot dropdown menu */}
                    {(canEditStudentProfile || canDeleteStudents || canArchiveStudents || canResetPassword || canSuspendStudents) && (
                      <td className="px-3 py-4 whitespace-nowrap text-right">
                        <button
                          data-dropdown-trigger
                          ref={(el) => {
                            if (el) dropdownButtonRefs.current.set(student.id, el);
                          }}
                          onClick={(e) => handleOpenActionsDropdown(student.id, e.currentTarget)}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Actions"
                        >
                          <MoreVertical size={18} />
                        </button>

                        {/* Dropdown Menu - Rendered via Portal */}
                        {openActionsDropdown === student.id && createPortal(
                          <div
                            ref={actionsDropdownRef}
                            className="fixed w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-[9999]"
                            style={{
                              top: dropdownPosition.showAbove ? 'auto' : dropdownPosition.top,
                              bottom: dropdownPosition.showAbove ? window.innerHeight - dropdownPosition.top + 4 : 'auto',
                              left: Math.max(8, dropdownPosition.left),
                            }}
                          >
                            {/* View Details */}
                            <button
                              onClick={() => {
                                setStudentToView(student);
                                setShowViewStudentModal(true);
                                setOpenActionsDropdown(null);
                              }}
                              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <Eye size={16} className="text-gray-400" />
                              Voir les détails
                            </button>

                            {/* Edit Profile */}
                            {canEditStudentProfile && (
                              <button
                                onClick={() => {
                                  handleEditProfileClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <Edit size={16} className="text-gray-400" />
                                Modifier le profil
                              </button>
                            )}

                            {/* Physical Documents */}
                            {canEditStudentProfile && (
                              <button
                                onClick={() => {
                                  handlePhysicalDocsClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <FolderOpen size={16} className="text-emerald-500" />
                                Documents physiques
                              </button>
                            )}

                            {/* Reset Password - Now shown for all students */}
                            {canResetPassword && (
                              <button
                                onClick={() => {
                                  handleResetPasswordClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                              >
                                <KeyRound size={16} className="text-blue-500" />
                                Reinitialiser mot de passe
                              </button>
                            )}

                            {/* Divider before dangerous actions */}
                            {(canSuspendStudents || canArchiveStudents || canDeleteStudents) && (
                              <div className="border-t border-gray-100 my-1" />
                            )}

                            {/* Suspend - shown for active students */}
                            {canSuspendStudents && student.status === 'ACTIVE' && (
                              <button
                                onClick={() => {
                                  handleSuspendClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                              >
                                <Ban size={16} />
                                Suspendre le compte
                              </button>
                            )}

                            {/* Unsuspend - shown only for suspended students */}
                            {canSuspendStudents && student.status === 'SUSPENDED' && (
                              <button
                                onClick={() => {
                                  handleUnsuspendClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors"
                              >
                                <ShieldCheck size={16} />
                                Lever la suspension
                              </button>
                            )}

                            {/* Archive - shown for non-withdrawn, non-suspended students */}
                            {canArchiveStudents && student.status !== 'WITHDRAWN' && student.status !== 'SUSPENDED' && (
                              <button
                                onClick={() => {
                                  handleArchiveClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 transition-colors"
                              >
                                <Archive size={16} />
                                Archiver (Desistement)
                              </button>
                            )}

                            {/* Restore - shown only for withdrawn students */}
                            {canArchiveStudents && student.status === 'WITHDRAWN' && (
                              <button
                                onClick={() => {
                                  handleRestoreClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-green-600 hover:bg-green-50 transition-colors"
                              >
                                <RotateCcw size={16} />
                                Restaurer
                              </button>
                            )}

                            {/* Delete */}
                            {canDeleteStudents && (
                              <button
                                onClick={() => {
                                  handleDeleteClick(student);
                                  setOpenActionsDropdown(null);
                                }}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 size={16} />
                                Supprimer
                              </button>
                            )}
                          </div>,
                          document.body
                        )}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Page {page} sur {totalPages} — {totalStudents} étudiant{totalStudents > 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loading}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>
        </>
      )}

      {/* Modals */}
      <CreateStudentModal
        isOpen={showStudentModal}
        onClose={() => setShowStudentModal(false)}
        onSuccess={handleCreateSuccess}
        onBulkImport={canBulkCreateStudents ? () => setShowBulkImportModal(true) : undefined}
      />

      <CreateParentModal
        isOpen={showParentModal}
        onClose={() => setShowParentModal(false)}
        onSuccess={handleCreateSuccess}
      />

      <BulkImportStudentsModal
        isOpen={showBulkImportModal}
        onClose={() => setShowBulkImportModal(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && studentToDelete && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelDelete}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelDelete}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Warning icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertTriangle size={32} className="text-red-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Supprimer le compte
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    studentToDelete.role === 'STUDENT' ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    {studentToDelete.role === 'STUDENT' ? (
                      <GraduationCap size={20} className="text-green-600" />
                    ) : (
                      <Users size={20} className="text-purple-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToDelete.firstName} {studentToDelete.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToDelete.email}</p>
                  </div>
                </div>
              </div>

              {/* Warning message */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Attention : Cette action est irréversible</p>
                    <ul className="list-disc list-inside space-y-1 text-yellow-700">
                      <li>Toutes les données de l'utilisateur seront supprimées</li>
                      <li>L'historique et les documents seront perdus</li>
                      {studentToDelete.role === 'STUDENT' && studentToDelete.linkedParents && studentToDelete.linkedParents.length > 0 && (
                        <li>Les liens avec les comptes parents seront supprimés</li>
                      )}
                    </ul>
                  </div>
                </div>
              </div>

              {deleteError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                  <XCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-800">{deleteError}</p>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Suppression...</span>
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      <span>Supprimer</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Archive Confirmation Modal */}
      {studentToResetDeadline && (
        <ResetDeadlineModal
          user={studentToResetDeadline}
          isSubmitting={resettingDeadline}
          onConfirm={handleConfirmResetDeadline}
          onCancel={() => setStudentToResetDeadline(null)}
        />
      )}

      {showArchiveModal && studentToArchive && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelArchive}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelArchive}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Warning icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-orange-100 rounded-full">
                  <Archive size={32} className="text-orange-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Désistement / Archiver
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    studentToArchive.role === 'STUDENT' ? 'bg-green-100' : 'bg-purple-100'
                  }`}>
                    {studentToArchive.role === 'STUDENT' ? (
                      <GraduationCap size={20} className="text-green-600" />
                    ) : (
                      <Users size={20} className="text-purple-600" />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToArchive.firstName} {studentToArchive.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToArchive.email}</p>
                  </div>
                </div>
              </div>

              {/* Info message */}
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-medium mb-1">Archiver cet étudiant ?</p>
                    <ul className="list-disc list-inside space-y-1 text-orange-700">
                      <li>L'étudiant sera marqué comme "Désistement"</li>
                      <li>Son compte sera désactivé</li>
                      <li>Les données seront conservées pour référence</li>
                      <li>Cette action peut être annulée ultérieurement</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelArchive}
                  disabled={archiving}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmArchive}
                  disabled={archiving}
                  className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {archiving ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Archivage...</span>
                    </>
                  ) : (
                    <>
                      <Archive size={16} />
                      <span>Archiver</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Restore Confirmation Modal */}
      {showRestoreModal && studentToRestore && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelRestore}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelRestore}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <RotateCcw size={32} className="text-green-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réactiver l'étudiant
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <GraduationCap size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToRestore.firstName} {studentToRestore.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToRestore.email}</p>
                  </div>
                </div>
              </div>

              {/* Info message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Réactiver cet étudiant ?</p>
                    <ul className="list-disc list-inside space-y-1 text-green-700">
                      <li>L'étudiant sera marqué comme "Actif"</li>
                      <li>Son compte sera réactivé</li>
                      <li>Il pourra à nouveau se connecter</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelRestore}
                  disabled={restoring}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmRestore}
                  disabled={restoring}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {restoring ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Réactivation...</span>
                    </>
                  ) : (
                    <>
                      <RotateCcw size={16} />
                      <span>Réactiver</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Suspend Confirmation Modal */}
      {showSuspendModal && studentToSuspend && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelSuspend}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelSuspend}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Warning icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <Ban size={32} className="text-red-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Suspendre le compte
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <GraduationCap size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToSuspend.firstName} {studentToSuspend.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToSuspend.email}</p>
                  </div>
                </div>
              </div>

              {/* Hold type selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de blocage <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedHoldType}
                  onChange={(e) => setSelectedHoldType(e.target.value as HoldType)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="ACADEMIC">Blocage académique</option>
                  <option value="DISCIPLINARY">Blocage disciplinaire</option>
                  <option value="FINANCIAL">Blocage financier</option>
                </select>
              </div>

              {/* Reason (optional) */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif (optionnel)
                </label>
                <textarea
                  value={suspendReason}
                  onChange={(e) => setSuspendReason(e.target.value)}
                  placeholder="Indiquez la raison de la suspension..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                  rows={3}
                  maxLength={500}
                />
                <p className="text-xs text-gray-400 mt-1">{suspendReason.length}/500 caractères</p>
              </div>

              {/* Warning message */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-800">
                    <p className="font-medium mb-1">Attention</p>
                    <ul className="list-disc list-inside space-y-1 text-red-700">
                      <li>Le compte de l'étudiant sera suspendu</li>
                      <li>L'étudiant ne pourra plus se connecter</li>
                      <li>Cette action peut être annulée ultérieurement</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelSuspend}
                  disabled={suspending}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmSuspend}
                  disabled={suspending}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {suspending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Suspension...</span>
                    </>
                  ) : (
                    <>
                      <Ban size={16} />
                      <span>Suspendre</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Unsuspend Confirmation Modal */}
      {showUnsuspendModal && studentToUnsuspend && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelUnsuspend}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelUnsuspend}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <ShieldCheck size={32} className="text-green-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Lever la suspension
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-red-100">
                    <GraduationCap size={20} className="text-red-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToUnsuspend.firstName} {studentToUnsuspend.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToUnsuspend.email}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-red-100 text-red-800 rounded">
                      Suspendu
                    </span>
                  </div>
                </div>
              </div>

              {/* Info message */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-green-800">
                    <p className="font-medium mb-1">Lever la suspension ?</p>
                    <ul className="list-disc list-inside space-y-1 text-green-700">
                      <li>Tous les blocages actifs seront levés</li>
                      <li>L'étudiant sera marqué comme "Actif"</li>
                      <li>Il pourra à nouveau se connecter</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleCancelUnsuspend}
                  disabled={unsuspending}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmUnsuspend}
                  disabled={unsuspending}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {unsuspending ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Levée en cours...</span>
                    </>
                  ) : (
                    <>
                      <ShieldCheck size={16} />
                      <span>Lever la suspension</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Choice Modal - Student or Parent */}
      {showResetPasswordChoiceModal && studentForPasswordReset && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelPasswordChoice}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelPasswordChoice}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <KeyRound size={32} className="text-blue-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réinitialiser le mot de passe
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <GraduationCap size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentForPasswordReset.firstName} {studentForPasswordReset.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentForPasswordReset.email}</p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-500 text-center mb-4">
                Choisissez la méthode de réinitialisation
              </p>

              {/* Student options section */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Pour l'étudiant</p>

                {/* Option 1: Email */}
                <button
                  onClick={handleChooseStudentEmailReset}
                  className="w-full mb-2 p-3 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 transition-colors text-left flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-blue-100">
                    <Mail size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">Envoyer un email</p>
                    <p className="text-xs text-gray-400">
                      Un lien de réinitialisation sera envoyé par email
                    </p>
                  </div>
                </button>

                {/* Option 2: Temp password */}
                <button
                  onClick={handleChooseStudentTempPasswordReset}
                  className="w-full p-3 border border-gray-200 rounded-lg hover:border-green-500 hover:bg-green-50 transition-colors text-left flex items-center gap-3"
                >
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <Key size={20} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-gray-900 text-sm">Mot de passe temporaire</p>
                    <p className="text-xs text-gray-400">
                      Créer un mot de passe à partager avec l'étudiant
                    </p>
                  </div>
                </button>
              </div>

              {/* Choice: Parents */}
              {studentForPasswordReset.linkedParents && studentForPasswordReset.linkedParents.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Pour un parent</p>

                  {studentForPasswordReset.linkedParents.map((parent) => (
                    <button
                      key={parent.id}
                      onClick={() => handleChooseParentReset(parent)}
                      className="w-full mb-2 p-3 border border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors text-left flex items-center gap-3"
                    >
                      <div className="h-10 w-10 rounded-full flex items-center justify-center bg-purple-100">
                        <UserCog size={20} className="text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">
                          {parent.firstName} {parent.lastName}
                        </p>
                        <p className="text-xs text-gray-400">
                          {parent.relationship === 'FATHER' ? 'Père' :
                           parent.relationship === 'MOTHER' ? 'Mère' :
                           parent.relationship === 'GUARDIAN' ? 'Tuteur' :
                           parent.relationship === 'OTHER' ? 'Autre' : parent.relationship} - Mot de passe temporaire
                        </p>
                      </div>
                      <Key size={16} className="text-purple-500" />
                    </button>
                  ))}
                </div>
              )}

              {/* Cancel button */}
              <button
                onClick={handleCancelPasswordChoice}
                className="w-full mt-4 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Student Temp Password Reset Modal */}
      {showResetStudentTempPasswordModal && studentToResetPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelResetStudentTempPassword}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelResetStudentTempPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-green-100 rounded-full">
                  <KeyRound size={32} className="text-green-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Mot de passe temporaire - Étudiant
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <GraduationCap size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToResetPassword.firstName} {studentToResetPassword.lastName}
                    </p>
                    {studentToResetPassword.email ? (
                      <p className="text-sm text-gray-500">{studentToResetPassword.email}</p>
                    ) : studentToResetPassword.username ? (
                      <p className="text-sm text-gray-500">{studentToResetPassword.username}</p>
                    ) : null}
                  </div>
                </div>
              </div>

              {/* Error message */}
              {resetStudentTempPasswordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{resetStudentTempPasswordError}</p>
                  </div>
                </div>
              )}

              {/* Success - Show credentials */}
              {studentTempResetResult && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-800">
                        Mot de passe temporaire créé avec succès
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Partagez ces identifiants avec l'étudiant. Il devra changer son mot de passe à la première connexion.
                  </p>

                  {/* Credentials */}
                  <div className="space-y-3">
                    {/* Email - only show if student has email */}
                    {studentToResetPassword.email && (
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div>
                          <span className="text-xs text-gray-500 block">Email</span>
                          <span className="font-mono font-medium text-gray-900 text-sm">{studentToResetPassword.email}</span>
                        </div>
                        <button
                          onClick={() => studentToResetPassword.email && copyStudentCredential(studentToResetPassword.email, 'email')}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Copier"
                        >
                          {copiedStudentField === 'email' ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <Copy size={18} />
                          )}
                        </button>
                      </div>
                    )}

                    {/* Login ID (username) - always show */}
                    {studentToResetPassword.username && (
                      <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div>
                          <span className="text-xs text-gray-500 block">Identifiant de connexion</span>
                          <span className="font-mono font-medium text-gray-900 text-sm">{studentToResetPassword.username}</span>
                        </div>
                        <button
                          onClick={() => studentToResetPassword.username && copyStudentCredential(studentToResetPassword.username, 'username')}
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                          title="Copier"
                        >
                          {copiedStudentField === 'username' ? (
                            <CheckCircle size={18} className="text-green-600" />
                          ) : (
                            <Copy size={18} />
                          )}
                        </button>
                      </div>
                    )}

                    {/* Temp Password */}
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 block">Mot de passe temporaire</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-gray-900">
                            {showStudentTempPassword ? studentTempResetResult.tempPassword : '••••••••'}
                          </span>
                          <button
                            onClick={() => setShowStudentTempPassword(!showStudentTempPassword)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title={showStudentTempPassword ? 'Masquer' : 'Afficher'}
                          >
                            {showStudentTempPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => copyStudentCredential(studentTempResetResult.tempPassword, 'password')}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedStudentField === 'password' ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                    <div className="flex gap-2">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        Ce mot de passe temporaire ne sera plus visible après la fermeture de cette fenêtre.
                        Assurez-vous de le noter ou de le communiquer à l'étudiant.
                      </p>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleCancelResetStudentTempPassword}
                    className="w-full mt-4 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Fermer
                  </button>
                </>
              )}

              {/* Before reset - Info message and buttons */}
              {!studentTempResetResult && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <AlertCircle size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-green-800">
                        <p className="font-medium mb-1">Un mot de passe temporaire sera créé</p>
                        <ul className="list-disc list-inside space-y-1 text-green-700">
                          <li>Un nouveau mot de passe temporaire sera généré</li>
                          <li>L'étudiant devra le changer à la première connexion</li>
                          <li>Vous pourrez copier les identifiants pour les partager</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelResetStudentTempPassword}
                      disabled={resettingStudentTempPassword}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmResetStudentTempPassword}
                      disabled={resettingStudentTempPassword}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {resettingStudentTempPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Création...</span>
                        </>
                      ) : (
                        <>
                          <Key size={16} />
                          <span>Créer le mot de passe</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent Password Reset Modal - Temporary Password */}
      {showResetParentPasswordModal && parentToResetPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelResetParentPassword}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelResetParentPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-purple-100 rounded-full">
                  <KeyRound size={32} className="text-purple-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réinitialiser le mot de passe du parent
              </h3>

              {/* Parent info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-purple-100">
                    <UserCog size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {parentToResetPassword.firstName} {parentToResetPassword.lastName}
                    </p>
                    <p className="text-sm text-gray-500">
                      {parentToResetPassword.relationship === 'FATHER' ? 'Père' :
                       parentToResetPassword.relationship === 'MOTHER' ? 'Mère' :
                       parentToResetPassword.relationship === 'GUARDIAN' ? 'Tuteur' :
                       parentToResetPassword.relationship === 'OTHER' ? 'Autre' : parentToResetPassword.relationship}
                    </p>
                  </div>
                </div>
              </div>

              {/* Error message */}
              {resetParentPasswordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{resetParentPasswordError}</p>
                  </div>
                </div>
              )}

              {/* Success - Show credentials */}
              {parentResetResult && (
                <>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                    <div className="flex gap-3">
                      <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                      <p className="text-sm text-green-800">
                        Mot de passe temporaire créé avec succès
                      </p>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">
                    Partagez ces identifiants avec le parent. Il devra changer son mot de passe à la première connexion.
                  </p>

                  {/* Credentials */}
                  <div className="space-y-3">
                    {/* Username */}
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div>
                        <span className="text-xs text-gray-500 block">Nom d'utilisateur</span>
                        <span className="font-mono font-medium text-gray-900">{parentResetResult.username}</span>
                      </div>
                      <button
                        onClick={() => parentResetResult.username && copyParentCredential(parentResetResult.username, 'username')}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedParentField === 'username' ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>

                    {/* Temp Password */}
                    <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                      <div className="flex-1">
                        <span className="text-xs text-gray-500 block">Mot de passe temporaire</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium text-gray-900">
                            {showParentTempPassword ? parentResetResult.tempPassword : '••••••••'}
                          </span>
                          <button
                            onClick={() => setShowParentTempPassword(!showParentTempPassword)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                            title={showParentTempPassword ? 'Masquer' : 'Afficher'}
                          >
                            {showParentTempPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => copyParentCredential(parentResetResult.tempPassword, 'password')}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title="Copier"
                      >
                        {copiedParentField === 'password' ? (
                          <CheckCircle size={18} className="text-green-600" />
                        ) : (
                          <Copy size={18} />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Warning */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
                    <div className="flex gap-2">
                      <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800">
                        Ce mot de passe temporaire ne sera plus visible après la fermeture de cette fenêtre.
                        Assurez-vous de le noter ou de le communiquer au parent.
                      </p>
                    </div>
                  </div>

                  {/* Close button */}
                  <button
                    onClick={handleCancelResetParentPassword}
                    className="w-full mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    Fermer
                  </button>
                </>
              )}

              {/* Before reset - Info message and buttons */}
              {!parentResetResult && (
                <>
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
                    <div className="flex gap-3">
                      <AlertCircle size={20} className="text-purple-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-purple-800">
                        <p className="font-medium mb-1">Un mot de passe temporaire sera créé</p>
                        <ul className="list-disc list-inside space-y-1 text-purple-700">
                          <li>Un nouveau mot de passe temporaire sera généré</li>
                          <li>Le parent devra le changer à la première connexion</li>
                          <li>Vous pourrez copier les identifiants pour les partager</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={handleCancelResetParentPassword}
                      disabled={resettingParentPassword}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={handleConfirmResetParentPassword}
                      disabled={resettingParentPassword}
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {resettingParentPassword ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          <span>Création...</span>
                        </>
                      ) : (
                        <>
                          <Key size={16} />
                          <span>Créer le mot de passe</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Confirmation Modal */}
      {showResetPasswordModal && studentToResetPassword && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCancelResetPassword}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCancelResetPassword}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <KeyRound size={32} className="text-blue-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                Réinitialiser le mot de passe
              </h3>

              {/* Student info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full flex items-center justify-center bg-green-100">
                    <GraduationCap size={20} className="text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {studentToResetPassword.firstName} {studentToResetPassword.lastName}
                    </p>
                    <p className="text-sm text-gray-500">{studentToResetPassword.email}</p>
                  </div>
                </div>
              </div>

              {/* Success message */}
              {resetPasswordSuccess && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <CheckCircle size={20} className="text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-800">{resetPasswordSuccess}</p>
                  </div>
                </div>
              )}

              {/* Error message */}
              {resetPasswordError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{resetPasswordError}</p>
                  </div>
                </div>
              )}

              {/* Info message */}
              {!resetPasswordSuccess && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex gap-3">
                    <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-blue-800">
                      <p className="font-medium mb-1">Un email sera envoyé à l'étudiant</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-700">
                        <li>Un lien de réinitialisation sera envoyé par email</li>
                        <li>Le lien sera valide pendant 24 heures</li>
                        <li>L'étudiant devra définir un nouveau mot de passe</li>
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {/* Buttons */}
              {!resetPasswordSuccess && (
                <div className="flex gap-3">
                  <button
                    onClick={handleCancelResetPassword}
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleConfirmResetPassword}
                    disabled={resettingPassword}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {resettingPassword ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        <span>Envoi...</span>
                      </>
                    ) : (
                      <>
                        <Mail size={16} />
                        <span>Envoyer le lien</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Parent Credentials Modal */}
      {showCredentialsModal && selectedParent && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/50 transition-opacity"
              onClick={handleCloseCredentials}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6 z-10">
              {/* Close button */}
              <button
                onClick={handleCloseCredentials}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <X size={20} />
              </button>

              {/* Icon and Title */}
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-blue-100 rounded-full">
                  <Key size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Identifiants de connexion
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedParent.firstName} {selectedParent.lastName}
                  </p>
                </div>
              </div>

              {/* Info */}
              <p className="text-sm text-gray-600 mb-4">
                Partagez ces identifiants avec le parent. Il devra changer son mot de passe à la première connexion.
              </p>

              {/* Credentials */}
              <div className="space-y-3">
                {/* Username */}
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div>
                    <span className="text-xs text-gray-500 block">Nom d'utilisateur</span>
                    <span className="font-mono font-medium text-gray-900">{selectedParent.username}</span>
                  </div>
                  <button
                    onClick={() => selectedParent.username && copyToClipboard(selectedParent.username, 'username')}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Copier"
                  >
                    {copiedField === 'username' ? (
                      <CheckCircle size={18} className="text-green-600" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>

                {/* Temp Password */}
                <div className="flex items-center justify-between bg-gray-50 p-3 rounded-lg border border-gray-200">
                  <div className="flex-1">
                    <span className="text-xs text-gray-500 block">Mot de passe temporaire</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-medium text-gray-900">
                        {showPassword ? selectedParent.tempPassword : '••••••'}
                      </span>
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title={showPassword ? 'Masquer' : 'Afficher'}
                      >
                        {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => selectedParent.tempPassword && copyToClipboard(selectedParent.tempPassword, 'password')}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    title="Copier"
                  >
                    {copiedField === 'password' ? (
                      <CheckCircle size={18} className="text-green-600" />
                    ) : (
                      <Copy size={18} />
                    )}
                  </button>
                </div>
              </div>

              {/* Close button */}
              <div className="mt-6">
                <button
                  onClick={handleCloseCredentials}
                  className="w-full px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Profile Validation Modal */}
      {selectedValidationId && (
        <ProfileValidationModal
          isOpen={showValidationModal}
          onClose={() => {
            setShowValidationModal(false);
            setSelectedValidationId(null);
          }}
          validationRequestId={selectedValidationId}
          onValidationComplete={handleValidationComplete}
        />
      )}

      {/* View Student Details Modal */}
      <ViewStudentModal
        isOpen={showViewStudentModal}
        onClose={() => {
          setShowViewStudentModal(false);
          setStudentToView(null);
        }}
        student={studentToView}
      />

      {/* Direct Edit Student Profile Modal */}
      {studentToEdit && (
        <DirectEditStudentProfileModal
          isOpen={showEditProfileModal}
          onClose={() => {
            setShowEditProfileModal(false);
            setStudentToEdit(null);
          }}
          studentUserId={studentToEdit.id}
          studentName={`${studentToEdit.firstName} ${studentToEdit.lastName}`}
          onSuccess={() => loadData(true)}
        />
      )}

      {/* Student Physical Documents Tracking Modal */}
      {studentForPhysicalDocs && (
        <StudentPhysicalDocumentsTrackingModal
          isOpen={showPhysicalDocsModal}
          onClose={() => {
            setShowPhysicalDocsModal(false);
            setStudentForPhysicalDocs(null);
          }}
          student={studentForPhysicalDocs}
          onSuccess={() => loadData(true)}
        />
      )}

      {/* Student Credentials Modal - for students without email who haven't logged in */}
      {showStudentCredentialsModal && studentForCredentials && (
        <StudentCredentialsModal
          student={studentForCredentials}
          onClose={() => {
            setShowStudentCredentialsModal(false);
            setStudentForCredentials(null);
          }}
        />
      )}

      {/* Bulk Export Credentials Modal */}
      {showBulkExportModal && (
        <BulkExportCredentialsModal
          students={allStudents}
          academicYears={academicYears}
          currentAcademicYear={currentAcademicYear}
          onClose={() => setShowBulkExportModal(false)}
        />
      )}
    </div>
  );
};

export default StudentManagementPage;
